import React, { useState, useEffect, useLayoutEffect } from "react";
import { Switch } from "@headlessui/react";
import { Calendar, Repeat, FileText, ArrowLeft, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { RootState, useAppDispatch } from "../../../redux/store";
import { createUnavailability } from "../../../redux/features/unavailability/api";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setGlobalBackHandler } from "../../../hooks";
import { useLocation } from "react-router";
import { getDepartments } from "../../../redux/features/common";

const days = ["S", "M", "T", "W", "T", "F", "S"];
const fullDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function UnavailabilityFormComponent() {
    const [startDate, setStartDate] = useState<string>();
    const dispatch = useAppDispatch();
    const { unAvailability } = useSelector((state: RootState) => state.unavailability);
    const [fromTime, setFromTime] = useState<string | undefined>(undefined);
    const [toTime, setToTime] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [allDay, setAllDay] = useState<boolean>(false);
    const [repeating, setRepeating] = useState<boolean>(false);
    const [repeatDays, setRepeatDays] = useState<number[]>([2]); // Tuesday selected by default
    const [overwriteExistingSlots, setOverwriteExistingSlots] = useState<boolean>(false);
    const [note, setNote] = useState("");
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [selectedDepartment, setSelectedDepartment] = useState<string>("");
    const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);
    const location = useLocation();
    const { departments } = useSelector((state: RootState) => state.common);

    // Get today's date in YYYY-MM-DD format for date input min attribute
    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        if (departments.length === 0) {
            dispatch(getDepartments());
        }

        // if (departments.length > 0) {
        //     setSelectedDepartment(departments[0].department_code || departments[0].code || "");
        // }
    }, [dispatch]);

    // Reset fromTime and toTime if allDay is true
    useEffect(() => {
        if (allDay) {
            setFromTime(undefined);
            setToTime(undefined);
        }
    }, [allDay]);

    // Clear errors when repeating changes
    useEffect(() => {
        if (!repeating) {
            setErrors((prev) => ({ ...prev, endDate: "" }));
        }
    }, [repeating]);

    const toggleDay = (index: number) => {
        setRepeatDays((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
    };

    const getSelectedDaysForApi = (): string => {
        return repeatDays.map((i) => fullDays[i]).join(",");
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        // Validate start date is not in the past
        if (startDate && startDate < today) {
            newErrors.startDate = "Start date cannot be in the past";
        }

        // Validate end date is required when repeating is enabled
        if (repeating && !endDate) {
            newErrors.endDate = "End date is required when repeating is enabled";
        }

        // Validate end date is not before start date
        if (startDate && endDate && endDate < startDate) {
            newErrors.endDate = "End date cannot be before start date";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const Line = () => <hr className="border-gray-200" />;

    const handleSubmit = () => {
        if (!validateForm()) {
            toast.error("Please fix the errors before submitting");
            return;
        }

        const payload = {
            start_date: startDate,
            from_time: fromTime,
            to_time: toTime,
            end_date: endDate,
            all_day: allDay ? (1 as 1) : (0 as 0),
            repeating: repeating ? (1 as 1) : (0 as 0),
            repeat_days: repeating ? getSelectedDaysForApi() : "", // reset if repeating false
            note,
            overwrite_existing_slots: overwriteExistingSlots ? (1 as 1) : (0 as 0),
            department_code: selectedDepartment || undefined,
        };
        dispatch(createUnavailability(payload))
            .unwrap()
            .then((response) => {
                if (response && response.status === false) {
                    toast.error(response.message || "Failed to create unavailability");
                    return;
                }
                toast.success(response?.message || "Unavailability created successfully!");
                // Navigate back after successful submit
                window.history.back();
            })
            .catch((error) => {
                toast.error(error || "Failed to create unavailability");
            });
    };

    const handleBackClick = () => {
        window.history.back();
    };

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Unavailability"));

        if (location.state?.fromCalendar === true) {
            setGlobalBackHandler(() => {
                window.history.back();
            });
        }
    }, [dispatch]);

    return (
        <div className="p-4 space-y-0 bg-white rounded-lg shadow-md max-w-md mx-auto">
            {/* Back Button and Page Title for Web */}
            <div className="flex items-center justify-between mb-6 md:flex hidden">
                <button
                    onClick={handleBackClick}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                    <ArrowLeft size={20} />
                    <span>Back</span>
                </button>
                <h1 className="text-2xl font-bold text-gray-800">Unavailability</h1>
            </div>

            {/* Date */}
            <div className="flex justify-between items-center py-3">
                <div className="flex items-center gap-2">
                    <Calendar className="text-gray-600" />
                    <span className="font-medium text-gray-800">Date</span>
                </div>
                <div className="flex flex-col items-end">
                    <input
                        type="date"
                        value={startDate}
                        min={today}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            if (errors.startDate) {
                                setErrors((prev) => ({ ...prev, startDate: "" }));
                            }
                        }}
                        className={`mt-1 w-[3/4] rounded-md border p-1 text-xs text-gray-800 focus:ring focus:ring-gray-200 ${
                            errors.startDate
                                ? "border-red-500 focus:border-red-500"
                                : "border-gray-300 focus:border-gray-500"
                        }`}
                    />
                    {errors.startDate && (
                        <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
                            <AlertCircle size={12} />
                            <span>{errors.startDate}</span>
                        </div>
                    )}
                </div>
            </div>
            <Line />

            {/* All Day */}
            <div className="flex justify-between items-center py-3 text-gray-800">
                <span>All Day</span>
                <Switch
                    checked={allDay}
                    onChange={setAllDay}
                    className={`${
                        allDay ? "bg-gray-600" : "bg-gray-300"
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform bg-white rounded-full transition ${
                            allDay ? "translate-x-5" : "translate-x-1"
                        }`}
                    />
                </Switch>
            </div>
            <Line />

            {/* From */}
            {!allDay && (
                <>
                    <div className="flex justify-between items-center py-3 ">
                        <span className="text-gray-800">From</span>
                        <input
                            type="time"
                            value={fromTime}
                            onChange={(e) => setFromTime(e.target.value + ":00")} // add seconds HH:mm:ss
                            className="mt-1 w-[3/4] rounded-md border border-gray-300 p-1 text-xs text-gray-800 focus:border-gray-500 focus:ring focus:ring-gray-200"
                        />
                    </div>
                    <Line />

                    {/* To */}
                    <div className="flex justify-between items-center py-3">
                        <span className="text-gray-800">To</span>
                        <input
                            type="time"
                            value={toTime}
                            onChange={(e) => setToTime(e.target.value + ":00")}
                            className="mt-1 w-[3/4] rounded-md border border-gray-300 p-1 text-xs text-gray-800 focus:border-gray-500 focus:ring focus:ring-gray-200"
                        />
                    </div>
                    <Line />
                </>
            )}

            {/* Repeating Slot */}
            <div className="flex justify-between items-center py-3">
                <div className="flex items-center gap-2">
                    <Repeat className="text-gray-600" />
                    <span className="font-medium text-gray-800">Repeating Slot</span>
                </div>
                <Switch
                    checked={repeating}
                    onChange={setRepeating}
                    className={`${
                        repeating ? "bg-gray-600" : "bg-gray-300"
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform bg-white rounded-full transition ${
                            repeating ? "translate-x-5" : "translate-x-1"
                        }`}
                    />
                </Switch>
            </div>
            <Line />

            {/* Repeats */}
            {repeating && (
                <>
                    <div className="py-3">
                        <label className="font-medium block mb-2 text-gray-800">Repeats</label>
                        <div className="flex space-x-2 mb-1">
                            {days.map((d, i) => (
                                <button
                                    key={i}
                                    onClick={() => toggleDay(i)}
                                    className={`w-8 h-8 rounded-full border ${
                                        repeatDays.includes(i) ? "bg-gray-600 text-white" : "bg-white text-gray-800"
                                    }`}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>
                        {/* <p className="text-sm text-gray-800">Weekly</p> */}
                    </div>
                    <Line />
                </>
            )}

            {/* Overwrite Existing Slots */}
            {/* <div className="flex justify-between items-center py-3 text-gray-800">
        <span>Overwrite Existing Slots</span>
        <Switch
          checked={overwriteExistingSlots}
          onChange={setOverwriteExistingSlots}
          className={`${
            overwriteExistingSlots ? "bg-gray-600" : "bg-gray-300"
          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
        >
          <span
            className={`inline-block h-4 w-4 transform bg-white rounded-full transition ${
              overwriteExistingSlots ? "translate-x-5" : "translate-x-1"
            }`}
          />
        </Switch>
      </div>
      <Line /> */}

            {/* Ends On - only when repeating is enabled */}
            {repeating && (
                <>
                    <div className="flex justify-between items-center py-3">
                        <div className="flex items-center gap-2">
                            <Calendar className="text-gray-600" />
                            <span className="font-medium text-gray-800">
                                Ends On
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                        </div>
                        <div className="flex flex-col items-end">
                            <input
                                type="date"
                                value={endDate}
                                min={startDate || today}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    if (errors.endDate) {
                                        setErrors((prev) => ({ ...prev, endDate: "" }));
                                    }
                                }}
                                className={`mt-1 w-[3/4] rounded-md border p-1 text-xs text-gray-800 focus:ring focus:ring-gray-200 ${
                                    errors.endDate
                                        ? "border-red-500 focus:border-red-500"
                                        : "border-gray-300 focus:border-gray-500"
                                }`}
                            />
                            {errors.endDate && (
                                <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
                                    <AlertCircle size={12} />
                                    <span>{errors.endDate}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <Line />
                </>
            )}

            {/* Note */}
            <div className="py-3">
                <div className="flex items-center gap-2 mb-1">
                    <FileText className="text-gray-600" />
                    <span className="font-medium text-gray-800">Note</span>
                </div>
                <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 mt-2"
                    rows={3}
                    placeholder="Enter note..."
                />
            </div>
            <Line />

            {/* Additional Information Accordion */}
            <div className="py-3">
                <button
                    onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                    className="flex items-center justify-between w-full text-left"
                >
                    <span className="font-medium text-gray-800">Additional Information</span>
                    {isAccordionOpen ? (
                        <ChevronDown className="text-gray-600" size={20} />
                    ) : (
                        <ChevronRight className="text-gray-600" size={20} />
                    )}
                </button>

                {isAccordionOpen && (
                    <div className="mt-3 space-y-3">
                        {/* Department Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                            <select
                                value={selectedDepartment}
                                onChange={(e) => setSelectedDepartment(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-gray-500 focus:ring focus:ring-gray-200"
                            >
                                <option value="">Select a department</option>
                                {departments.map((dept) => (
                                    <option
                                        key={dept.os_department_id || dept.code}
                                        value={dept.department_code || dept.code || ""}
                                    >
                                        {dept.department_name || dept.name || "Unknown Department"}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Submit button */}
            <div className="pt-4">
                <button
                    onClick={handleSubmit}
                    className="w-full rounded border border-blue-600 text-blue-600 px-4 py-2 bg-transparent hover:bg-blue-50"
                >
                    Submit
                </button>
            </div>
        </div>
    );
}
